import { useCallback, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

/** A4 portrait aspect ratio (width / height). */
export const A4_PORTRAIT_RATIO = 210 / 297;

/**
 * Observes the referenced element and returns the largest integer width at which a box of the given
 * aspect ratio (`width / height`) fits entirely within it — constrained by both the element's width
 * and its height. Useful for sizing a fixed-ratio preview (e.g. an A4 PDF page) so the whole thing
 * stays visible without clipping or distortion.
 *
 * Uses a callback ref rather than a RefObject so measuring still works when the target element is
 * mounted conditionally, e.g. only after a loading state has finished.
 *
 * @example
 * const [previewRef, previewWidth] = useAspectFitWidth(A4_PORTRAIT_RATIO);
 * return <div ref={previewRef}>{previewWidth > 0 && <Preview width={previewWidth} />}</div>;
 */
export const useAspectFitWidth = (aspectRatio: number): [(node: HTMLElement | null) => void, number] => {
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  const setRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();

    if (!node) {
      observerRef.current = null;
      return;
    }

    observerRef.current = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;

      if (rect) {
        setSize({ width: rect.width, height: rect.height });
      }
    });

    observerRef.current.observe(node);
  }, []);

  const fitWidth = Math.floor(Math.min(width, height * aspectRatio));

  return [setRef, fitWidth];
};
